import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Project } from "store/appData";
import { useLocalStorage } from "utils/useLocalStorage";

const VALID_VALUES = ["id", "name", "id_and_name"] as const;
export type ProjectNameDisplay = (typeof VALID_VALUES)[number];

const STORAGE_KEY = "datasetViewer_projectNameDisplay";
const DEFAULT_DISPLAY: ProjectNameDisplay = "name";

export function formatProjectLabel(
  project: Project,
  mode: ProjectNameDisplay
): string {
  const regionPrefixedId = `${project.region.toUpperCase()}${project.id}`;
  switch (mode) {
    case "id":
      return regionPrefixedId;
    case "id_and_name":
      return `${regionPrefixedId} - ${project.name}`;
    case "name":
      return project.name;
  }
}

interface DatasetViewerSettingsContextValue {
  projectNameDisplay: ProjectNameDisplay;
  setProjectNameDisplay: (value: ProjectNameDisplay) => void;
}

const DatasetViewerSettingsContext =
  createContext<DatasetViewerSettingsContextValue | null>(null);

export function DatasetViewerSettingsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [projectNameDisplay, setProjectNameDisplay] =
    useState<ProjectNameDisplay>(DEFAULT_DISPLAY);

  useLocalStorage<ProjectNameDisplay>({
    key: STORAGE_KEY,
    data: projectNameDisplay,
    onLoad: (value) => {
      if ((VALID_VALUES as readonly string[]).includes(value)) {
        setProjectNameDisplay(value);
      }
    },
  });

  const value = useMemo(
    () => ({ projectNameDisplay, setProjectNameDisplay }),
    [projectNameDisplay]
  );

  return (
    <DatasetViewerSettingsContext.Provider value={value}>
      {children}
    </DatasetViewerSettingsContext.Provider>
  );
}

export function useDatasetViewerSettings() {
  const ctx = useContext(DatasetViewerSettingsContext);
  if (!ctx) {
    throw new Error(
      "useDatasetViewerSettings must be used inside DatasetViewerSettingsProvider"
    );
  }
  return ctx;
}
